import React, { useState, useRef, useEffect } from 'react';

interface CarouselProps {
  children: React.ReactNode[];
  itemsToShow?: number;
  spacing?: number;
  showArrows?: boolean;
  showDots?: boolean;
}

export const Carousel: React.FC<CarouselProps> = ({
  children,
  itemsToShow = 3,
  spacing = 16,
  showArrows = true,
  showDots = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [itemWidth, setItemWidth] = useState(0);
  const [visibleItems, setVisibleItems] = useState(itemsToShow);
  
  // Update sizes on resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const newContainerWidth = containerRef.current.offsetWidth;
        
        // Adjust visible items based on screen width
        let newVisibleItems = itemsToShow;
        const width = window.innerWidth;
        
        if (width < 640) {
          newVisibleItems = 1; // Mobile: 1 item
        } else if (width < 768) {
          newVisibleItems = Math.min(2, itemsToShow); // Small tablet: 2 items max
        } else if (width < 1024) {
          newVisibleItems = Math.min(2, itemsToShow); // Tablet: 2 items max
        } else {
          newVisibleItems = itemsToShow; // Desktop: use prop value
        }
        
        setVisibleItems(newVisibleItems);
        setItemWidth((newContainerWidth - (spacing * (newVisibleItems - 1))) / newVisibleItems);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [itemsToShow, spacing]);
  
  const isBeginning = currentIndex === 0;
  const isEnd = currentIndex >= children.length - visibleItems;
  
  const previous = () => {
    if (currentIndex <= 0) return;
    setCurrentIndex(currentIndex - 1);
  };
  
  const next = () => {
    if (currentIndex >= children.length - visibleItems) return;
    setCurrentIndex(currentIndex + 1);
  };
  
  // Touch event handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    if (distance > 50) {
      next();
    }
    
    if (distance < -50) {
      previous();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };
  
  // Calculate transform position
  const position = -currentIndex * (itemWidth + spacing);
  
  return (
    <div className="relative px-4">
      {/* Previous button */}
      {showArrows && (
        <button
          className={`absolute left-0 top-1/2 z-10 -translate-y-1/2 flex items-center justify-center w-10 h-10 bg-white rounded-full shadow-md border border-gray-200 ${
            isBeginning ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
          }`}
          onClick={previous}
          disabled={isBeginning}
          aria-label="Föregående"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      
      {/* Container */}
      <div 
        ref={containerRef}
        className="overflow-hidden px-5"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className="flex transition-transform duration-300 ease-out"
          style={{ 
            transform: `translateX(${position}px)`,
          }}
        >
          {React.Children.map(children, (child, index) => (
            <div
              key={index}
              className="flex-shrink-0"
              style={{ 
                width: `${itemWidth}px`,
                marginRight: index < children.length - 1 ? `${spacing}px` : 0
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
      
      {/* Next button */}
      {showArrows && (
        <button
          className={`absolute right-0 top-1/2 z-10 -translate-y-1/2 flex items-center justify-center w-10 h-10 bg-white rounded-full shadow-md border border-gray-200 ${
            isEnd ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
          }`}
          onClick={next}
          disabled={isEnd}
          aria-label="Nästa"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
      
      {/* Dots for pagination */}
      {showDots && children.length > visibleItems && (
        <div className="flex justify-center mt-4 space-x-2">
          {Array.from({ length: children.length - visibleItems + 1 }).map((_, i) => (
            <button
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentIndex ? 'bg-green-600' : 'bg-gray-300'
              }`}
              onClick={() => goToSlide(i)}
              aria-label={`Gå till bild ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}; 